import { useTemplateStore } from '@/store/template.store';
import { TypedEventEmitter } from '@/utils/TypedEventEmitter';
import type { ESelectionKind } from '@/domain/selection/ESelectionKind';
import type { IEntityRepository } from '@/application/ports/IEntityRepository';
import type { ISelectionState } from '@/application/ports/ISelectionState';
import type { AField } from '@/domain/field/AField';

// Passthrough onto the Pinia template store — the port abstractions
// exist so Editor2DController stays testable against a fake. Same
// class satisfies both IEntityRepository (entities) and ISelectionState
// (UI selection) because the store already owns both concerns.
class PiniaEntityRepository implements IEntityRepository, ISelectionState {
    private readonly _store: ReturnType<typeof useTemplateStore>;
    private readonly _events = new TypedEventEmitter<{ afterUpdate: AField }>();

    constructor() {
        this._store = useTemplateStore();
    }

    onAfterUpdate(callback: (entity: AField) => void): () => void {
        return this._events.subscribe('afterUpdate', callback);
    }

    add(entity: AField): void {
        this._store.addField(entity);
    }

    get(id: string): AField | undefined {
        return this._store.getFieldById(id);
    }

    getAll(): AField[] {
        return this._store.selectedTemplateConfiguration.fields;
    }

    getSelectedId(): string | undefined {
        return this._store.selectedFieldId;
    }

    getSelectedTextRange(): { end: number; start: number } | undefined {
        return this._store.selectedTextIndexes ?? undefined;
    }

    getSelectionKind(): ESelectionKind {
        return this._store.selectionKind;
    }

    remove(id: string): void {
        const field = this._store.getFieldById(id);
        if (!field) {
            return;
        }
        this._store.removeField(field);
    }

    reorderBackward(id: string): void {
        const field = this._store.getFieldById(id);
        if (!field) {
            return;
        }
        this._store.bringFieldBackward(field);
    }

    reorderForward(id: string): void {
        const field = this._store.getFieldById(id);
        if (!field) {
            return;
        }
        this._store.bringFieldForward(field);
    }

    reorderToBack(id: string): void {
        const field = this._store.getFieldById(id);
        if (!field) {
            return;
        }
        this._store.bringFieldToBack(field);
    }

    reorderToFront(id: string): void {
        const field = this._store.getFieldById(id);
        if (!field) {
            return;
        }
        this._store.bringFieldToFront(field);
    }

    replace(entities: AField[]): void {
        this._store.replaceTemplate(entities);
    }

    setSelectedId(id: string | undefined): void {
        this._store.setSelectedFieldId(id);
    }

    setSelectedTextRange(range: { end: number; start: number } | undefined): void {
        this._store.selectedTextIndexes = range;
    }

    update(entity: AField): void {
        this._store.updateField(entity);
        this._events.emit('afterUpdate', entity);
    }
}

export { PiniaEntityRepository };
